"use client";
import Link from "next/link";
import { ArrowRight, Quote } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { analystCoverage as data } from "@/lib/data/press";

const accent = "var(--color-emerald)";

export function AnalystCoverage() {
  return (
    <Section id="analyst-coverage" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-emerald)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <div className="mt-10 glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[rgb(var(--border)/0.18)] bg-[rgb(var(--border)/0.04)]">
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Firm</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Report</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))] hidden md:table-cell">Coverage</th>
                  </tr>
                </thead>
                <tbody>
                  {data.reports.map((r) => (
                    <tr key={r.firm + r.report} className="border-b border-[rgb(var(--border)/0.08)]">
                      <td className="px-4 py-3 align-top font-medium">{r.firm}</td>
                      <td className="px-4 py-3 align-top text-[rgb(var(--fg-muted))]">{r.report}</td>
                      <td className="px-4 py-3 align-top hidden md:table-cell"><span className="inline-flex px-2 py-0.5 rounded text-xs font-bold" style={{ background: `color-mix(in oklch, ${accent} 18%, transparent)`, color: accent }}>{r.coverage}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Reveal>
        <Reveal delay={200}>
          <div className="mt-10">
            <h3 className="text-lg mb-3">Analyst Commentary</h3>
            <div className="grid sm:grid-cols-2 gap-5">
              {data.commentary.map((c, i) => (
                <div key={i} className="glass rounded-2xl p-6" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 12%, transparent)` }}>
                  <Quote className="h-5 w-5 opacity-30 mb-2" style={{ color: accent }} />
                  <p className="text-sm text-[rgb(var(--fg-muted))] leading-relaxed italic">&ldquo;{c.quote}&rdquo;</p>
                  <div className="mt-3 text-xs font-semibold" style={{ color: accent }}>— {c.source}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={500}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link key={c.label} href={c.href} className={i === 0 ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm" : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"}>
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
